import React, { useState, useEffect } from 'react';
import { X, MessageSquare, Plus, Trash2, Edit2, Save, Loader2 } from 'lucide-react';
import { notesAPI } from '../../services/api';
import { toast } from 'sonner';

const NotesModal = ({ isOpen, onClose, entityType, entityId, entityName }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    if (isOpen && entityId) {
      fetchNotes();
    }
  }, [isOpen, entityId]);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const response = await notesAPI.getNotes(entityType, entityId);
      setNotes(response.data.notes || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      setSaving(true);
      const response = await notesAPI.create({
        entity_type: entityType,
        entity_id: entityId,
        content: newNote.trim()
      });
      setNotes([response.data, ...notes]);
      setNewNote('');
      toast.success('Note added');
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateNote = async (noteId) => {
    if (!editContent.trim()) return;
    
    try {
      setSaving(true);
      await notesAPI.update(noteId, editContent.trim());
      setNotes(notes.map(n => 
        n.id === noteId ? { ...n, content: editContent.trim(), updated_at: new Date().toISOString() } : n
      ));
      setEditingId(null);
      setEditContent('');
      toast.success('Note updated');
    } catch (error) {
      console.error('Error updating note:', error);
      toast.error('Failed to update note');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    
    try {
      await notesAPI.delete(noteId);
      setNotes(notes.filter(n => n.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--background-paper)] rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl">
          <div className="flex items-center gap-2 text-white">
            <MessageSquare className="w-5 h-5" />
            <h3 className="font-semibold">Notes</h3>
            {entityName && (
              <span className="text-blue-100 text-sm ml-2">- {entityName}</span>
            )}
          </div>
          <button 
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Add Note */}
        <div className="p-4 border-b bg-[var(--background-subtle)]">
          <div className="flex gap-2">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note..."
              className="flex-1 px-3 py-2 border border-[var(--border-primary)] rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-[#D4AF37] resize-none text-sm"
              rows={2}
              disabled={saving}
            />
            <button
              onClick={handleAddNote}
              disabled={!newNote.trim() || saving}
              className="px-4 py-2 bg-[#E6C965] text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 self-end"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Add
            </button>
          </div>
        </div>

        {/* Notes List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-8 text-[var(--text-muted)]">
              <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No notes yet</p>
              <p className="text-sm">Add your first note above</p>
            </div>
          ) : (
            notes.map((note) => (
              <div 
                key={note.id} 
                className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow"
              >
                {editingId === note.id ? (
                  <div className="space-y-2">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--border-primary)] rounded-lg focus:ring-2 focus:ring-blue-500 resize-none text-sm"
                      rows={3}
                      autoFocus
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setEditingId(null);
                          setEditContent('');
                        }}
                        className="px-3 py-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleUpdateNote(note.id)}
                        disabled={!editContent.trim() || saving}
                        className="px-3 py-1 text-sm bg-[#E6C965] text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                      >
                        <Save className="w-3 h-3" />
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-[var(--text-primary)] whitespace-pre-wrap">{note.content}</p>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                      <div className="text-xs text-[var(--text-muted)]">
                        <span>{note.created_by_name || 'Owner'}</span>
                        <span className="mx-1">•</span>
                        <span>{formatDate(note.created_at)}</span>
                        {note.updated_at && (
                          <span className="text-gray-400 ml-1">(edited)</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setEditingId(note.id);
                            setEditContent(note.content);
                          }}
                          className="p-1 text-gray-400 hover:text-[#D4AF37] transition-colors"
                          title="Edit note"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="p-1 text-gray-400 hover:text-[var(--error)] transition-colors"
                          title="Delete note"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Note Icon Button - reusable component for triggering the modal
export const NoteIconButton = ({ entityType, entityId, entityName, noteCount = 0, onOpen }) => {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onOpen(entityType, entityId, entityName);
      }}
      className="relative p-1.5 text-gray-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-lg transition-all"
      title="View/Add notes"
      data-testid={`note-icon-${entityType}-${entityId}`}
    >
      <MessageSquare className="w-4 h-4" />
      {noteCount > 0 && (
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#E6C965] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
          {noteCount > 9 ? '9+' : noteCount}
        </span>
      )}
    </button>
  );
};

export default NotesModal;
